import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import {
  AgeBand,
  InjectionPoint,
  InsulinProduct,
  InsulinScheduleView
} from '../../types/domain';
import InsulinProductSelect, { ProductSelection } from '../components/InsulinProductSelect';
import InjectionSitePicker from '../components/InjectionSitePicker';

import {
  getAgeBand,
  getAgeBandLabel,
  getAvailableRegions,
  getInsulinTypeOptions,
  getPointLabel,
  getRegionGrid,
  getRotationWarning,
  SAME_POINT_WARNING_THRESHOLD,
  consecutiveUseOfPoint
} from '../../lib/domain-data';

export default function InjectInsulin() {
  const { user, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve glucoseValue if passed from check-glucose page
  const passedGlucoseValue = location.state?.glucoseValue as number | undefined;
  // The measurement context belongs to that reading, and this page is what
  // writes the row on the check-glucose -> inject-insulin path (DF-03).
  const passedMeasurementContext = location.state?.measurementContext as string | undefined;

  const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<InjectionPoint | null>(null);
  const [suggestedPoint, setSuggestedPoint] = useState<InjectionPoint | null>(null);
  const [recentPoints, setRecentPoints] = useState<InjectionPoint[]>([]);
  const [lipoPoints, setLipoPoints] = useState<InjectionPoint[]>([]);
  const [lipodystrophy, setLipodystrophy] = useState(false);
  const [insulinTypeId, setInsulinTypeId] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [injectionDate, setInjectionDate] = useState<string>('');
  const [injectionTime, setInjectionTime] = useState<string>('');
  const [product, setProduct] = useState<ProductSelection>({ kind: 'none' });
  const [products, setProducts] = useState<InsulinProduct[]>([]);
  const [schedule, setSchedule] = useState<InsulinScheduleView[]>([]);
  const [insulinDose, setInsulinDose] = useState<number | ''>(1);

  const [isSavingInjection, setIsSavingInjection] = useState(false);
  const [saveInjectionError, setSaveInjectionError] = useState<string | null>(null);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setInjectionDate(local.toISOString().slice(0, 10));
    setInjectionTime(local.toISOString().slice(11, 16));
  }, []);

  /**
   * Next point to suggest: the first free cell of the region used least
   * recently, so rotation moves between limbs rather than shuffling within
   * one. A point is (region, row, col) now, so a flat index no longer works.
   */
  const nextSuggestion = (band: AgeBand, used: InjectionPoint[]): InjectionPoint | null => {
    const regions = getAvailableRegions(band);
    if (regions.length === 0) {
      return null;
    }
    const rank = (regionId: string) => {
      const i = used.findIndex(u => u.regionId === regionId);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    const coldest = [...regions].sort((a, b) => rank(b.id) - rank(a.id))[0];

    const grid = getRegionGrid(coldest.id, band);
    if (!grid) {
      return null;
    }
    const inRegion = used.filter(u => u.regionId === coldest.id);
    for (let row = 1; row <= grid.rows; row++) {
      for (let col = 1; col <= grid.cols; col++) {
        if (!inRegion.some(u => u.row === row && u.col === col)) {
          return { regionId: coldest.id, row, col };
        }
      }
    }
    return { regionId: coldest.id, row: 1, col: 1 };
  };

  const loadRotation = async (band: AgeBand) => {
    if (!user) {
      return;
    }
    const history = await dataService.getHistory(user.id);
    const used: InjectionPoint[] = history
      .filter(r => r.regionId && r.pointRow && r.pointCol)
      .slice(0, 8)
      .map(r => ({ regionId: r.regionId as string, row: r.pointRow as number, col: r.pointCol as number }));
    setRecentPoints(used);
    setSuggestedPoint(nextSuggestion(band, used));

    // Points the patient has already flagged, so the picker can warn them off
    // rather than quietly offering the same spot again (FR-11).
    const flagged = history
      .filter(r => r.lipodystrophy && r.regionId && r.pointRow && r.pointCol)
      .map(r => ({ regionId: r.regionId as string, row: r.pointRow as number, col: r.pointCol as number }));
    setLipoPoints(flagged);
  };

  useEffect(() => {
    const band = getAgeBand(profile?.birth_date);
    if (band && band !== ageBand) {
      setAgeBand(band);
      void loadRotation(band);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, ageBand]);

  const handleSelectPoint = (point: InjectionPoint) => {
    setSelectedPoint(point);
    setCompletionMessage(null);
    // The marker describes the site just chosen, so it must not carry over
    // from a previously selected one.
    setLipodystrophy(false);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionDate(event.target.value);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionTime(event.target.value);
  };

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    Promise.all([
      dataService.getInsulinProducts(),
      dataService.getSchedule(user.id)
    ]).then(([catalog, entries]) => {
      if (cancelled) {
        return;
      }
      setProducts(catalog);
      setSchedule(entries);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  /**
   * Pre-fills the insulin and dose from a configured slot (FR-04), so the
   * common case is one tap rather than retyping the regimen at every
   * injection. The patient can still override either field.
   */
  /** The name stored on the log row, from the catalog or the typed name. */
  /**
   * FR-04 replaces the six hardcoded injection types with the patient's own
   * schedule slots. A patient who has not configured one yet still needs
   * something to pick, so the legacy list is the fallback.
   */
  const slotOptions =
    schedule.length > 0
      ? schedule.map(entry => ({ id: entry.slot, label: entry.slotLabel }))
      : getInsulinTypeOptions();

  const resolveProductLabel = (): string => {
    if (product.kind === 'product') {
      return products.find(p => p.id === product.id)?.name ?? '';
    }
    if (product.kind === 'other') {
      return product.name.trim();
    }
    return '';
  };

  const applyScheduleSlot = (entry: InsulinScheduleView) => {
    setInsulinTypeId(entry.slot);
    setProduct(
      entry.productId
        ? { kind: 'product', id: entry.productId }
        : { kind: 'other', name: entry.customProductName ?? '' }
    );
    setInsulinDose(entry.dose);
  };

  const handleSaveInjection = async () => {
    const insulinLabel = resolveProductLabel();
    if (!ageBand || !selectedPoint || isSavingInjection || !user) {
      return;
    }
    if (!injectionDate || !injectionTime || !insulinLabel || insulinDose === '' || !insulinTypeId) {
      setSaveInjectionError('Mohon lengkapi semua data suntikan.');
      return;
    }

    setIsSavingInjection(true);
    setSaveInjectionError(null);
    try {
      await dataService.addInjection(user.id, {
        date: injectionDate,
        time: injectionTime,
        // Pre-FR-08 named-point id. New rows identify the site by grid
        // coordinate instead, so this stays null.
        siteId: null,
        regionId: selectedPoint.regionId,
        pointRow: selectedPoint.row,
        pointCol: selectedPoint.col,
        lipodystrophy,
        insulinTypeId,
        ageGroup: ageBand,
        insulinBrand: insulinLabel,
        insulinProductId: product.kind === 'product' ? product.id : null,
        insulinDose: Number(insulinDose),
        glucoseValue: passedGlucoseValue ?? undefined,
        measurementContext: passedGlucoseValue ? passedMeasurementContext ?? null : null
      });

      await dataService.updateLastPoint(user.id, ageBand, selectedPoint);

      const nextUsed = [selectedPoint, ...recentPoints].slice(0, 8);
      const next = nextSuggestion(ageBand, nextUsed);
      setCompletionMessage(
        next
          ? `Suntikan berhasil. Lokasi berikutnya disarankan di ${getPointLabel(next.regionId, next.row, next.col)}.`
          : 'Suntikan berhasil disimpan.'
      );

      setTimeout(() => navigate('/'), 2500);
    } catch {
      setSaveInjectionError('Terjadi kesalahan saat menyimpan riwayat suntikan.');
    } finally {
      setIsSavingInjection(false);
    }
  };

  // FR-10. Recomputed on every selection because it depends on the candidate
  // point, not just on history.
  const rotationWarning = getRotationWarning(selectedPoint, recentPoints);

  const renderSitePicker = () => {
    if (!ageBand) {
      return null;
    }
    return (
      <div className="screen-card" role="group" aria-labelledby="site-step-title">
        <div id="site-step-title" className="section-title">
          Pilih lokasi suntik
        </div>
        <div className="subtitle">
          Kelompok usia: {getAgeBandLabel(ageBand)}
        </div>

        {suggestedPoint ? (
          <div className="suggestion-text">
            <strong>
              Saran lokasi sekarang:{' '}
              {getPointLabel(suggestedPoint.regionId, suggestedPoint.row, suggestedPoint.col)}
            </strong>
            <div>Apabila lokasi suntik sebelumnya sama, maka dapat dipindah.</div>
          </div>
        ) : null}

        <InjectionSitePicker
          band={ageBand}
          value={selectedPoint}
          onChange={handleSelectPoint}
          suggested={suggestedPoint}
          recent={recentPoints}
          lipoPoints={lipoPoints}
        />

        {rotationWarning ? (
          <div
            className={`rotation-warning${
              consecutiveUseOfPoint(selectedPoint!, recentPoints) >=
              SAME_POINT_WARNING_THRESHOLD
                ? ' rotation-warning--strong'
                : ''
            }`}
            role="alert"
          >
            <span aria-hidden="true">⚠️</span>
            <span>{rotationWarning}</span>
          </div>
        ) : null}

        {selectedPoint ? (
          <label className="lipo-toggle">
            <input
              type="checkbox"
              checked={lipodystrophy}
              onChange={e => setLipodystrophy(e.target.checked)}
            />
            <span className="lipo-toggle-text">
              <span className="lipo-toggle-label">
                Ada benjolan / kulit menebal di titik ini
              </span>
              <span className="lipo-toggle-hint">
                Tandai bila kulit terasa keras atau menonjol (lipodistrofi). Ini
                dilaporkan ke dokter di laporan bulanan.
              </span>
            </span>
          </label>
        ) : null}
      </div>
    );
  };

  if (ageBand === null) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan data adik...</div>
      </div>
    );
  }

  const canSave = selectedPoint !== null;

  return (
    <div className="screen min-h-[100dvh] h-full flex flex-col">
      <div
        className="screen-card mb-4"
        role="group"
        aria-labelledby="inject-step-title"
      >
        <div id="inject-step-title" className="section-title">
          Suntik insulin
        </div>
        <div className="steps">
          <div className="step-pill active">1. Jenis insulin</div>
          <div className="step-pill">2. Lokasi suntik</div>
          <div className="step-pill">3. Simpan</div>
        </div>

        <div className="field-group">
          <div className="field">
            <label htmlFor="injection-date">Tanggal <span className="text-red-500">*</span></label>
            <input
              id="injection-date"
              type="date"
              value={injectionDate}
              onChange={handleDateChange}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="injection-time">Jam <span className="text-red-500">*</span></label>
            <input
              id="injection-time"
              type="time"
              value={injectionTime}
              onChange={handleTimeChange}
              required
            />
          </div>
        </div>

        {schedule.length > 0 ? (
          <div className="schedule-quickpick">
            <div className="subtitle">Pakai jadwal insulin</div>
            <div className="schedule-quickpick-row">
              {schedule.map(entry => (
                <button
                  key={entry.id}
                  type="button"
                  className="chip chip-compact"
                  onClick={() => applyScheduleSlot(entry)}
                >
                  {entry.slotLabel} · {entry.productLabel} {entry.dose}u
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="field-group">
          <div className="field">
            <label htmlFor="insulin-name">Nama insulin <span className="text-red-500">*</span></label>
            <InsulinProductSelect
              id="insulin-name"
              products={products}
              value={product}
              onChange={setProduct}
            />
          </div>
          <div className="field">
            <label htmlFor="insulin-dose">Dosis (unit) <span className="text-red-500">*</span></label>
            <input
              id="insulin-dose"
              type="number"
              min={0}
              inputMode="decimal"
              value={insulinDose}
              onChange={(e) => setInsulinDose(e.target.value === '' ? '' : Number(e.target.value))}
              required
            />
          </div>
        </div>

        <div className="subtitle">1. Pilih jenis suntikan <span className="text-red-500">*</span></div>
        <div className="option-grid mb-3">
          {slotOptions.map(option => (
            <button
              key={option.id}
              type="button"
              className={`chip ${insulinTypeId === option.id ? 'selected' : ''}`}
              onClick={() => setInsulinTypeId(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="screen-card flex-1 flex flex-col min-h-fit"
        role="group"
        aria-labelledby="inject-location-title"
      >
        <div id="inject-location-title" className="section-title text-[28px] mb-2 font-extrabold pb-1">
          Pilih lokasi suntikan
        </div>
        <div className="subtitle text-[16px]">
          2. Pilih titik di tubuh Adik kemudian simpan.
        </div>

        {renderSitePicker()}

        <div className="next-actions mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            className="btn-secondary w-full md:w-auto"
            onClick={() => navigate('/')}
          >
            Batal
          </button>
          <button
            type="button"
            className="btn-primary w-full md:w-auto flex-1"
            disabled={!canSave || isSavingInjection}
            aria-disabled={!canSave || isSavingInjection}
            aria-label="Simpan lokasi suntikan"
            onClick={handleSaveInjection}
          >
            {isSavingInjection ? 'Menyimpan...' : 'Simpan lokasi suntikan'}
          </button>
        </div>

        {completionMessage ? (
          <div className="completion-message font-medium text-emerald-700 bg-emerald-50 p-3 rounded mt-2 border border-emerald-100">{completionMessage}</div>
        ) : null}
        {saveInjectionError ? (
          <div className="subtitle mt-2 text-red-700 bg-red-50 p-2 rounded">
            {saveInjectionError}
          </div>
        ) : null}
      </div>
    </div>
  );
}
